import { FC, MouseEvent, useState } from 'react';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import FancySwitch from '@/components/FancySwitch';
import { Input, Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';
import { getOptimalFontSize } from '@/utils/coverImages/textDistribution';

import {
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  LabelText,
  ResetButton,
} from '../VariationOne';
import {
  ImageGrid,
  StyledTextArea,
  TextAreaBox,
  TextParametersBox,
} from '../VariationTwo';
import { Filler } from '../shared';
import { ActionBox, ActionTitle } from '../shared/styledComponents';
import GeneratedImages from './GeneratedImages';
import VariationsStyles from './VariationsStyles';

export const CHANNEL_NAME_COLORS = ['black', 'blue'];

export const CHANNEL_IMAGE_OPTIONS = ['custom', 'reddit'];

export interface ImageVariation {
  leftText: string;
  rightText: string;
  divider: string;
  leftBrakes: boolean;
  brakes: boolean;
  leftFontSize?: number;
  fontSize?: number;
  reversed?: boolean;
}

export const InputsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
`;

export const GridInner = styled.div<{ $noBottomPadding?: boolean }>`
  padding: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  &:nth-child(even) {
    border-left: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  }
  ${({ $noBottomPadding }) =>
    $noBottomPadding &&
    css`
      padding-bottom: 0;
    `}
`;

export const NameTextArea = styled(Textarea)`
  height: 98px;
`;

const HeadingsHolder = styled.div`
  flex: 1;
`;

getOptimalFontSize('some random text', {
  maxWidth: 530,
  maxHeight: 560,
  fontFamily: 'Gothic',
});

interface Props {
  inArticle?: boolean;
}

const VariationBoldTextText: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [brakes, setBrakes] = useState(false);
  const [leftBrakes, setLeftBrakes] = useState(false);
  const [leftText, setLeftText] = useState('');
  const [leftFontSize, setLeftFontSize] = useState('');
  const [text, setText] = useState('');
  const [fontSize, setFontSize] = useState('');
  const [isReversed, setIsReversed] = useState(false);

  const [variations, setVariations] = useState<ImageVariation[]>([]);

  const [images, setImages] = useState<string[]>();

  const onReset = () => {
    setVariations([]);
    setImages([]);
    setBrakes(false);
    setLeftBrakes(false);
    setLeftText('');
    setLeftFontSize('');
    setText('');
    setIsReversed(false);
    setFontSize('');
  };

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const newVariations = () => [
      {
        rightText: text,
        leftText: leftText,
        brakes: brakes,
        leftBrakes: leftBrakes,
        divider: 'black',
        fontSize: fontSize ? Number(fontSize) : undefined,
        leftFontSize: leftFontSize ? Number(leftFontSize) : undefined,
        reversed: isReversed,
      },
    ];

    setVariations(newVariations);
  };

  const isResetDisabled =
    !fontSize &&
    !text &&
    !brakes &&
    !leftText &&
    !leftFontSize &&
    !leftBrakes &&
    !isReversed;

  return (
    <>
      <Box>
        <Header>
          <HeadingsHolder>
            <Heading $type="button">Bold Text + Text</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attach Bold text on the left side and add text on the right with
              the necessary parameters
            </Text>
          </HeadingsHolder>
          <HeaderButtons>
            <FancySwitch
              isActive={isReversed}
              onClick={() => setIsReversed((prev) => !prev)}
              text="Reversed"
            />
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <>
            <ImageGrid>
              <TextParametersBox $reversed={!isReversed}>
                <TextAreaBox $reversed={!isReversed}>
                  <div>
                    <LabelText $type="smallHeadline">
                      {isReversed ? 'Right' : 'Left'} Text
                    </LabelText>
                    <StyledTextArea
                      onChange={(e) => setLeftText(e.target.value)}
                      value={leftText}
                      placeholder="Add text"
                    ></StyledTextArea>
                  </div>
                  <div>
                    <LabelText $type="smallHeadline">Font Size</LabelText>
                    <Input
                      value={leftFontSize}
                      onChange={(e) => setLeftFontSize(e.target.value)}
                      placeholder="Font Size (px)"
                      type="number"
                      min="0"
                    />
                  </div>
                  <div>
                    <ActionTitle $type="smallHeadline">
                      Text Parameters
                    </ActionTitle>
                    <ActionBox>
                      <Checkbox
                        checked={leftBrakes}
                        onChange={() => setLeftBrakes((prev) => !prev)}
                      >
                        Custom brakes
                      </Checkbox>
                    </ActionBox>
                  </div>
                </TextAreaBox>
              </TextParametersBox>
              <TextParametersBox $reversed={isReversed}>
                <TextAreaBox $reversed={isReversed}>
                  <div>
                    <LabelText $type="smallHeadline">
                      {isReversed ? 'Left' : 'Right'} Text
                    </LabelText>
                    <StyledTextArea
                      onChange={(e) => setText(e.target.value)}
                      value={text}
                      placeholder="Add text"
                    ></StyledTextArea>
                  </div>
                  <div>
                    <LabelText $type="smallHeadline">Font Size</LabelText>
                    <Input
                      value={fontSize}
                      onChange={(e) => setFontSize(e.target.value)}
                      placeholder="Font Size (px)"
                      type="number"
                      min="0"
                    />
                  </div>
                  <div>
                    <ActionTitle $type="smallHeadline">
                      Text Parameters
                    </ActionTitle>
                    <ActionBox>
                      <Checkbox
                        checked={brakes}
                        onChange={() => setBrakes((prev) => !prev)}
                      >
                        Custom brakes
                      </Checkbox>
                    </ActionBox>
                  </div>
                </TextAreaBox>
              </TextParametersBox>
            </ImageGrid>
          </>
        )}
      </Box>
      <Filler fontFamily="Gothic" />
      <VariationsStyles />
      {showContent && (
        <ButtonHolder>
          <Button onClick={generateVariations}>
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
    </>
  );
};

export default VariationBoldTextText;
